import React, { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { maxInitialSections } from './Sections.constants';
import { loadShowSection } from '../../api/pac/load-show-section';
import { queryKeys } from '../../config/use-query';
import { usePath, useVideoConfig } from '../../config/VideoAppContext';
import { useAxios } from '../../hooks/use-axios';
import { useItems } from '../../hooks/use-items';
import { usePacPageLink } from '../../hooks/use-pac-page-link';
import { useShowPosterImage } from '../../hooks/use-show-poster-image';
import { Highlight } from '../Highlight/Highlight';
import { Swimlane } from '../Swimlane/Swimlane';
import { SwimlaneTitle } from '../Swimlane/SwimlaneTitle';
import { Swipeable } from '../Swipeable/Swipeable';
import { ShowTeaser } from '../Teaser/ShowTeaser/ShowTeaser';
import { ShowElement } from '../TeaserElement/ShowElement';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const ShowSection = ({
  section,
  initialData = {},
  sectionIndex
}) => {
  const [wasIntersecting, setWasIntersecting] = useState(false);
  const drawContent = sectionIndex < maxInitialSections || wasIntersecting;
  const {
    previewDate,
    usePreview
  } = useVideoConfig();
  const {
    videoShowPath
  } = usePath();
  const sectionData = initialData.preloadedSections?.[section.id];
  const preloadedData = sectionData?.sectionType === 'ShowSection' ? sectionData : undefined;
  const axios = useAxios();
  const {
    data: plainShows = [],
    error,
    isLoading
  } = useQuery({
    queryKey: [queryKeys.ShowSection(section.id), {
      usePreview,
      previewDate
    }, axios],
    queryFn: () => loadShowSection(axios, section.id, usePreview, previewDate),
    initialData: preloadedData?.data
  });
  const {
    pickRandomElement,
    title,
    label,
    description
  } = section.representation;
  const shows = useItems(plainShows, pickRandomElement, preloadedData?.randomShows);
  const {
    showPosterImage,
    aspectRatio
  } = useShowPosterImage(shows);
  const showSection = !error && shows.length > 0 || isLoading;
  const detailLink = usePacPageLink(section);
  if (!showSection) {
    return /*#__PURE__*/_jsx(Fragment, {});
  }
  switch (section.representation.name) {
    case 'Swimlane':
      {
        return /*#__PURE__*/_jsxs(Swimlane, {
          rootMargin: "300px",
          onIntersectionChanged: isIntersecting => {
            if (isIntersecting) {
              setWasIntersecting(isIntersecting);
            }
          },
          children: [/*#__PURE__*/_jsx(SwimlaneTitle, {
            title: title ?? '',
            description: description,
            titleLink: detailLink
          }), /*#__PURE__*/_jsx(Swipeable, {
            placeholderAspectRatio: aspectRatio,
            analyticsTitle: title,
            children: drawContent && shows?.map((show, index) => /*#__PURE__*/_jsx(ShowTeaser, {
              show: show,
              index: index,
              showPosterImage: showPosterImage
            }, show.id))
          })]
        });
      }
    case 'ShowElement':
      {
        const [show] = shows;
        return /*#__PURE__*/_jsx(ShowElement, {
          labelText: label,
          show: show,
          title: title,
          index: 0
        });
      }
    case 'ShowPromotion':
      {
        const [firstShow] = shows;
        const showLink = firstShow ? videoShowPath(firstShow) : undefined;
        return showLink ? /*#__PURE__*/_jsx(Highlight, {
          highlightElementType: 'ShowPromotion',
          detailLink: showLink,
          linkState: {
            show: firstShow
          },
          title: title,
          description: description,
          imageUrl: section.representation.imageUrl,
          focalPoint: section.representation.focalPoint
        }) : /*#__PURE__*/_jsx(Fragment, {});
      }
    default:
      {
        return /*#__PURE__*/_jsx(Fragment, {});
      }
  }
};